// Quick-add: bare calories in, no name required — for when someone just needs
// the number logged. Two taps + a number (F1's fastest path). Optional macros
// stay collapsed behind a link — and their VALUES are Pro (the moved gate):
// free users see the titles with a small Pro tag that opens the paywall.
import { useState } from "react";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";
import { useToast } from "../lib/toast";
import { hapticSuccess } from "../lib/haptics";
import { defaultMealSlot } from "../lib/date";
import { useEntitlement } from "../lib/useEntitlement";
import { SheetShell } from "./SheetShell";
import { MealSlotPicker } from "./MealSlotPicker";
import { LockedField } from "./ProLock";
import { UnitInput } from "./ui/UnitInput";
import { useUnits } from "../lib/useUnits";
import { kcalToKj, kjToKcal } from "@shared/units";
import { Paywall } from "./Paywall";
import { Button } from "./ui/Button";
import { QUICK_ADD_NAME, type MealSlot } from "@shared/types";

interface Props {
  date: string; // YYYY-MM-DD
  /** Preselect this meal (the + the user tapped); falls back to time-of-day. */
  initialSlot?: MealSlot;
  onDone: () => Promise<void> | void;
  onClose: () => void;
}

export function QuickAddSheet({ date, initialSlot, onDone, onClose }: Props) {
  const t = useT();
  const toast = useToast();
  const { premium } = useEntitlement();
  const units = useUnits();
  const kjMode = units.prefs.energy === "kj";
  const [kcal, setKcal] = useState("");
  const [slot, setSlot] = useState<MealSlot>(initialSlot ?? defaultMealSlot(new Date().getHours()));
  const [showMacros, setShowMacros] = useState(false);
  const [protein, setProtein] = useState("");
  const [carbs, setCarbs] = useState("");
  const [fat, setFat] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);

  const num = (v: string): number | null => {
    const n = parseFloat(v.replace(",", "."));
    return Number.isFinite(n) && n >= 0 ? n : null;
  };
  const calories = num(kcal);
  const valid = calories !== null && calories > 0 && calories <= 10000;

  async function save() {
    if (!valid || busy) return;
    setBusy(true);
    try {
      await api.addFood({
        client_id: crypto.randomUUID(),
        eaten_on: date,
        source: "quick",
        plan_meal_slot: slot,
        name: QUICK_ADD_NAME,
        calories: Math.round(calories!),
        // Detail amounts are Pro — a free quick-add is the bare number.
        protein_g: premium ? num(protein) : null,
        carbs_g: premium ? num(carbs) : null,
        fat_g: premium ? num(fat) : null,
      });
      hapticSuccess();
      await onDone();
      onClose();
    } catch {
      toast.error(t("common.failed"));
      setBusy(false);
    }
  }

  return (
    <SheetShell onClose={onClose}>
      <h3 className="text-lg font-semibold mt-4 mb-4">{t("quick.title")}</h3>

      <div className="mb-4">
        <UnitInput
          label={kjMode ? `${t("food_sheet.calories")} (kJ)` : t("food_sheet.calories")}
          value={kcal}
          onChange={setKcal}
          toDisplay={(n) => (kjMode ? Math.round(kcalToKj(n)) : n)}
          fromDisplay={(n) => (kjMode ? kjToKcal(n) : n)}
          inputMode="numeric"
        />
      </div>

      <div className="mb-4">
        <MealSlotPicker value={slot} onChange={setSlot} />
      </div>

      {!showMacros ? (
        <button type="button" onClick={() => setShowMacros(true)} className="text-sm text-fg-muted mb-4 min-h-[44px]">
          {t("quick.add_macros")}
        </button>
      ) : (
        <div className="grid grid-cols-3 gap-2 mb-4">
          {([
            ["P", protein, setProtein],
            ["C", carbs, setCarbs],
            ["F", fat, setFat],
          ] as const).map(([label, value, set]) =>
            premium ? (
              <label key={label} className="block">
                <span className="text-sm text-fg-muted">{label} (g)</span>
                <input
                  className="input mt-1 tabular-nums"
                  type="text"
                  inputMode="decimal"
                  value={value}
                  onChange={(e) => set(e.target.value)}
                />
              </label>
            ) : (
              <LockedField key={label} label={`${label} (g)`} onUpgrade={() => setShowPaywall(true)} />
            ),
          )}
        </div>
      )}

      <Button fullWidth onClick={save} disabled={!valid || busy}>
        {busy ? t("common.loading") : t("today.add")}
      </Button>
      <div className="pb-2" />
      {showPaywall && <Paywall trigger="feature" onClose={() => setShowPaywall(false)} />}
    </SheetShell>
  );
}
